/**
 * The hole the finger is aiming at (deck page 3, "DROP BETWEEN 03 AND 04").
 *
 * Beat 3 of the storyboard is an *insertion gap*, not a shuffle: the list opens
 * a row-height slot at the place the lifted row would land, and it is drawn as
 * an empty version of a row — same width, same height, same radius — so the
 * board reads as one card missing rather than as a decorated divider. The
 * dashed 2px terracotta edge is the only dashed border in the app, which is
 * what makes "this is a target, not a player" legible at a glance.
 *
 * `slotUnderCard` below decides which slot the hole belongs to, and it is the
 * same answer dnd-kit uses for `over` — so the hole, the rank numbers and the
 * drop can never disagree.
 */
import type { CollisionDetection } from '@dnd-kit/core';
import type { PlayerId } from '@idgame/protocol';

/**
 * How far the lifted card must cover a row before that row gives way.
 *
 * The card sits under the finger and the hole sits under the card, so the
 * honest threshold is the halfway line — the row you are more than half over.
 * The extra tenth is hysteresis, and only that: a thumb resting on the
 * boundary would otherwise chatter between two slots.
 */
const DISPLACE = 0.6;

/**
 * Which slot the lifted row is aiming at. Rows are evenly pitched, so the whole
 * thing is one division: `aim` carries the answer between calls, because a
 * threshold that is not the halfway line has to remember which side it is on.
 */
export function slotUnderCard(
  order: readonly PlayerId[],
  aim: { current: number | null },
): CollisionDetection {
  return ({ collisionRect, droppableRects }) => {
    const first = order[0] ? droppableRects.get(order[0]) : undefined;
    const second = order[1] ? droppableRects.get(order[1]) : undefined;
    const pitch = first && second ? second.top - first.top : 0;
    if (!first || pitch <= 0) return [];

    const position = (collisionRect.top - first.top) / pitch;
    let slot = aim.current ?? Math.round(position);
    while (slot < order.length - 1 && position >= slot + DISPLACE) slot += 1;
    while (slot > 0 && position <= slot - DISPLACE) slot -= 1;
    aim.current = slot;

    const id = order[slot];
    return id ? [{ id }] : [];
  };
}

/** Ranks read as two digits here, so the label's width never jumps. */
const pad = (n: number) => String(n).padStart(2, '0');

export function DropGap({
  rank,
  top,
  height,
}: {
  /** The rank the lifted row would take on release, 1-based. */
  rank: number;
  /** Offset from the top of the list's content box, in px. */
  top: number;
  height: number;
}) {
  // The gap names its neighbours, because the number the player is aiming for
  // is the one the row above already carries. At the very top there is no
  // neighbour above to name.
  const label = rank <= 1 ? 'Drop at 01' : `Drop between ${pad(rank - 1)} and ${pad(rank)}`;

  return (
    <li
      aria-hidden="true"
      data-testid="rank-drop-gap"
      data-drop-rank={rank}
      // The hole slides between lanes rather than teleporting: the card is now
      // tracking the finger continuously, and a target that jumps while the
      // thing aiming at it glides is the one part of the gesture that still
      // read as steppy.
      style={{ top, height, transition: 'top 140ms cubic-bezier(0.2, 0, 0, 1)' }}
      className="pointer-events-none absolute inset-x-0 flex list-none items-center justify-center rounded-card border-2 border-dashed border-terracotta bg-cream text-[0.625rem] font-semibold tracking-[0.14em] text-terracotta uppercase"
    >
      {label}
    </li>
  );
}
